import { forwardRef, ReactElement } from 'react';
// next
import NextLink from 'next/link';
// @mui
import { Box } from '@mui/material';
// hooks
import useLocales from '../../../hooks/useLocales';
// config
import { ICON } from '../../../config';
// type
import { NavItemProps } from '@common/@types/nav';
//
import Iconify from '../../Iconify';
import { ListItemStyle, ListItemStyleProps } from './style';
import { isExternalLink } from '..';

// ----------------------------------------------------------------------

// HANDLE SHOW ITEM BY ROLE
const ListItem = forwardRef<HTMLButtonElement & HTMLAnchorElement, ListItemStyleProps>(
  (props, ref) => (
    <ListItemStyle {...props} ref={ref}>
      {props.children}
    </ListItemStyle>
  )
);

export const NavItemRoot = forwardRef<HTMLButtonElement & HTMLAnchorElement, NavItemProps>(
  ({ item, active, open, onMouseEnter, onMouseLeave }, ref) => {
    const { translate } = useLocales();

    const { title, path, icon, children, disabled, blacklisted } = item;

    if (blacklisted) return <></>;

    if (children) {
      return (
        <ListItem
          ref={ref}
          open={open}
          activeRoot={active}
          onMouseEnter={onMouseEnter}
          onMouseLeave={onMouseLeave}
          disabled={disabled}
        >
          <NavItemContent icon={icon} title={translate(title)} children={children} />
        </ListItem>
      );
    }

    return isExternalLink(path) ? (
      <NextLink href={path} passHref style={{ textDecoration: 'none' }}>
        <ListItem disabled={disabled}>
          <NavItemContent icon={icon} title={translate(title)} children={children} />
        </ListItem>
      </NextLink>
    ) : (
      <NextLink href={path} passHref style={{ textDecoration: 'none' }}>
        <ListItem activeRoot={active} disabled={disabled}>
          <NavItemContent icon={icon} title={translate(title)} children={children} />
        </ListItem>
      </NextLink>
    );
  }
);

// ----------------------------------------------------------------------

export const NavItemSub = forwardRef<HTMLButtonElement & HTMLAnchorElement, NavItemProps>(
  ({ item, active, open, onMouseEnter, onMouseLeave }, ref) => {
    const { translate } = useLocales();

    const { title, path, icon, children, disabled, blacklisted } = item;

    if (blacklisted) return <></>;

    if (children) {
      return (
        <ListItem
          ref={ref}
          subItem
          disableRipple
          open={open}
          activeSub={active}
          onMouseEnter={onMouseEnter}
          onMouseLeave={onMouseLeave}
          disabled={disabled}
        >
          <NavItemContent icon={icon} title={translate(title)} children={children} subItem />
        </ListItem>
      );
    }

    return isExternalLink(path) ? (
      <NextLink href={path} passHref style={{ textDecoration: 'none' }}>
        <ListItem subItem disabled={disabled}>
          <NavItemContent icon={icon} title={translate(title)} children={children} subItem />
        </ListItem>
      </NextLink>
    ) : (
      <NextLink href={path} passHref style={{ textDecoration: 'none' }}>
        <ListItem disableRipple activeSub={active} subItem disabled={disabled}>
          <NavItemContent icon={icon} title={translate(title)} children={children} subItem />
        </ListItem>
      </NextLink>
    );
  }
);

// ----------------------------------------------------------------------

type NavItemContentProps = {
  title: string;
  icon?: ReactElement;
  children?: { title: string; path: string }[];
  subItem?: boolean;
};

function NavItemContent({ icon, title, children, subItem }: NavItemContentProps) {
  const { translate } = useLocales();

  return (
    <>
      {icon && (
        <Box
          component="span"
          sx={{
            mr: 1,
            width: ICON.NAVBAR_ITEM_HORIZONTAL,
            height: ICON.NAVBAR_ITEM_HORIZONTAL,
            '& svg': { width: '100%', height: '100%' },
          }}
        >
          {icon}
        </Box>
      )}

      {translate(title)}

      {children && (
        <Iconify
          icon={subItem ? 'eva:chevron-right-fill' : 'eva:chevron-down-fill'}
          sx={{
            ml: 0.5,
            width: ICON.NAVBAR_ITEM_HORIZONTAL,
            height: ICON.NAVBAR_ITEM_HORIZONTAL,
          }}
        />
      )}
    </>
  );
}
